import FormField from '@polestar/component-warehouse-react/components/form-field/FormField';
import Grid from '@polestar/component-warehouse-react/components/grid/Grid';
import GridColumn from '@polestar/component-warehouse-react/components/grid/GridColumn';
import Selectable, { SelectableVariants, selectableVariantsStyles, } from '@polestar/component-warehouse-react/components/selectable/Selectable';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import styled from '@emotion/styled';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Spans from '@polestar/component-warehouse-react/tokens/spans/Spans';
import React, { forwardRef, Fragment, useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import ScrollViewEnhanced from '@polestar/component-warehouse-react/components/scroll-view-enhanced/ScrollViewEnhanced';
import { m as motion } from 'framer-motion';
import { MotionControlsVariants } from '@polestar/component-warehouse-react/components/motion/useMotionControls';
import merge from 'deepmerge';
import { genericMotionVariants } from '@polestar/component-warehouse-react/components/motion/helpers';
import { createTransition, MotionDurationsSeconds, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Opacities from '@polestar/component-warehouse-react/tokens/opacities/Opacities';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
const containerVariants = merge(genericMotionVariants, {
    [MotionControlsVariants.visible]: {
        transition: {
            duration: MotionDurationsSeconds.fast,
            staggerChildren: MotionDurationsSeconds.xFast,
        },
    },
    [MotionControlsVariants.exit]: {
        transition: {
            duration: MotionDurationsSeconds.xFast,
            staggerChildren: MotionDurationsSeconds.xFast,
        },
    },
});
const selectableMotionVariants = {
    [MotionControlsVariants.enter]: {
        opacity: Opacities.none,
        x: Spacings.xxLarge,
    },
    [MotionControlsVariants.visible]: {
        opacity: Opacities.full,
        x: 0,
        transition: createTransition({ duration: 0.8, ease: MotionEasings.easeOut }),
    },
    [MotionControlsVariants.exit]: {
        opacity: Opacities.none,
    },
};
const SelectableField = forwardRef(({ onChange, onFocus, onBlur, error, errorMessage, hint, label, disabled, name, buttonTitle, buttonTitleChecked, buttonAriaLabel, options, fieldProps, size = Sizes.medium, variant = SelectableVariants.default, 
// ignored on purpose:
// eslint-disable-next-line @typescript-eslint/no-unused-vars
trailing, gridGap, gridColumnMinWidth, tooltip, monochrome = false, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    const memoizedId = useSSRSafeId('selectable-field', props.id);
    const memoizedFieldId = `${memoizedId}-container`;
    const gridSpan = (!!gridColumnMinWidth
        ? Spans.one
        : size === Sizes.small
            ? Spans.two
            : size === Sizes.medium
                ? Spans.three
                : { mobile: 4, tablet: 4 });
    const wrappedOnChange = useCallback((changedChecked, changedName) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(changedChecked, changedName);
    }, [onChange]);
    const handleFocus = useCallback((e) => {
        setFocused(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }, [onFocus]);
    const handleBlur = useCallback((e) => {
        setFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    }, [onBlur]);
    const isList = variant === SelectableVariants.list ||
        variant === SelectableVariants.listIconLeft ||
        variant === SelectableVariants.listIconRight ||
        variant === SelectableVariants.listMinimalRound;
    const isMinimalRound = variant === SelectableVariants.minimalRound;
    const isMinimalNumbered = variant === SelectableVariants.minimalNumbered;
    const GridWrapper = (isMinimalRound || isMinimalNumbered
        ? ListWrapperHorizontal
        : isList
            ? ListWrapper
            : NestedGrid);
    const isGrid = !isList && !isMinimalRound && !isMinimalNumbered;
    const ColumnWrapper = isGrid ? GridColumn : Fragment;
    const columnProps = (isGrid ? { span: gridSpan } : {});
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { required, id: ignoredId, ...containerProps } = props;
    if (isMinimalNumbered && size === Sizes.medium) {
        size = Sizes.small;
    }
    return (React.createElement(FormField, { ...fieldProps, ref: ref, id: memoizedFieldId, error: error, errorMessage: errorMessage || (typeof error === 'string' && error), hint: hint, label: label, border: false, disabled: disabled, required: required, focused: focused, isTextInput: false, tooltip: tooltip },
        React.createElement(GridWrapper, { nested: true, hasLabel: !!label, hasHint: !!hint, gridGap: gridGap, gridColumnMinWidth: gridColumnMinWidth, size: isList || isMinimalRound || isMinimalNumbered ? size : undefined, gapSize: isMinimalNumbered ? Spacings.xSmall : undefined, variants: containerVariants, ...containerProps, "data-testid": "selectable-field" }, options === null || options === void 0 ? void 0 : options.map((selectableProps, index) => (React.createElement(ColumnWrapper, { ...columnProps, key: `${name}-selectable-${index}` },
            React.createElement(React.Fragment, null,
                React.createElement(Selectable, { ...aria({
                        label: variant !== SelectableVariants.withButton
                            ? buttonAriaLabel === null || buttonAriaLabel === void 0 ? void 0 : buttonAriaLabel({
                                name: selectableProps.name || name,
                                checked: !!selectableProps.checked,
                                ...selectableProps,
                            })
                            : undefined,
                    }), 
                    // TODO: should this not use `${memoizedId}-${index}`?
                    id: `${name}-selectable-${index}`, variant: variant, buttonTitle: buttonTitle, buttonTitleChecked: buttonTitleChecked, buttonAriaLabel: buttonAriaLabel, ...selectableProps, name: selectableProps.name || name, disabled: selectableProps.disabled || disabled, checked: selectableProps.checked, onChange: wrappedOnChange, onFocus: handleFocus, onBlur: handleBlur, variants: selectableMotionVariants, minimalRoundSize: (isMinimalRound || isMinimalNumbered) && size === Sizes.xSmall
                        ? Sizes.xSmall
                        : size === Sizes.small
                            ? size
                            : Sizes.medium, monochrome: monochrome }))))))));
});
SelectableField.displayName = 'SelectableField';
export default SelectableField;
const NestedGrid = motion(styled(Grid)(({ hasLabel, hasHint, gridGap, gridColumnMinWidth }) => css(css `
        margin: ${hasLabel ? Spacings.large : 0} 0 ${hasHint ? Spacings.large : 0};
        width: 100%;
      `, !!gridGap && responsiveStyles({ gridGap }, { gridGap: (value) => `${value} !important;` }), !!gridColumnMinWidth &&
    responsiveStyles({
        gridTemplateColumns: gridColumnMinWidth,
    }, {
        gridTemplateColumns: (value) => `repeat(auto-fit, minmax(${value}, 1fr)) !important;`,
    }))));
const ListWrapper = motion(styled.div(({ hasLabel, hasHint, size, gapSize }) => {
    const parsedGapSize = gapSize || (size && selectableVariantsStyles[size].gap);
    return css `
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      margin: calc(${hasLabel ? Spacings.large : 0} - ${parsedGapSize}) 0
        ${hasHint ? Spacings.large : 0};
      width: 100%;
      height: calc(100% + ${parsedGapSize});

      & > * {
        margin: ${parsedGapSize} 0 0 0;
      }
    `;
}));
const ListWrapperHorizontal = motion(styled(ScrollViewEnhanced)(({ size, gapSize }) => {
    const parsedGapSize = gapSize || (size && selectableVariantsStyles[size].gap);
    return css `
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      width: 100%;
      gap: ${parsedGapSize};
    `;
}));
